import {
  CommonQuestionKeys,
  ExpressionType,
  Field,
  FieldType,
  MathOperator,
  PaymentMethod,
  TextInputType,
} from '@bybeam/platform-types';
import { inList, literal, or, sum } from '../../ast/utils.js';
import { RACES } from './options.js';
import { StandardComponents } from './types.js';

const OTHER_HELPER_TEXT = {
  indigenousFirstNationAmericanIndianAndOrAlaskanNative:
    'Enter, for example, Aztec, Blackfeet Tribe, Doyon, Mayan, Muscogee (Creek) Nation, Native Village of Barrow Inupiat Tribal Government, Navajo Nation, Tlingit, etc.',
  asian: 'Enter, for example, Afghani, Bangladeshi, Malaysian, etc.',
  blackAndOrAfricanAmerican: 'Enter, for example, Tanzanian, South African, Sudanese etc.',
  'hispanicOrLatino/a/eOrLatinAmerican':
    'Enter, for example, Chilean, Ecuadorian, Guatemalan, Honduran, etc.',
  middleEasternOrNorthAfrican: 'Enter, for example, Algerian, Kurdish, Iraqi, etc.',
  nativeHawaiianOrPacificIslander: 'Enter, for example, Palauan, Tahitian, Chuukese, etc.',
  whiteAndOrEuropean: 'Enter, for example, Dutch, Norwegian, Scottish, etc.',
};

const QUESTIONS = {
  [CommonQuestionKeys.APPLICATION_ASSISTANCE_HELPER]: {
    key: CommonQuestionKeys.APPLICATION_ASSISTANCE_HELPER,
    copy: {
      title: 'Did someone help you fill out this application?',
      intro:
        'Please provide the following information about the person that helped you complete this application.',
    },
    skippable: true,
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Helper Name',
        key: 'submission.assistance.helper.name',
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Organization (if applicable)',
        key: 'submission.assistance.helper.organization',
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Email,
        copy: 'Helper Email',
        key: 'submission.assistance.helper.email',
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Phone,
        copy: 'Helper Phone Number',
        key: 'submission.assistance.helper.phone',
        validation: { required: false },
      },
    ],
  },
  [CommonQuestionKeys.DATE_OF_BIRTH]: {
    key: CommonQuestionKeys.DATE_OF_BIRTH,
    copy: { title: 'What is your Date of Birth?' },
    fields: [
      {
        type: FieldType.Date,
        copy: 'Date of Birth',
        key: CommonQuestionKeys.DATE_OF_BIRTH,
        validation: { required: true, allowedRange: 'past' },
        props: { helperText: 'Example: 06/01/2004' },
      },
    ],
  },
  [CommonQuestionKeys.GENDER]: {
    key: CommonQuestionKeys.GENDER,
    copy: { title: 'What is your current gender identity?' },
    skippable: true,
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Gender',
        key: 'gender.gender',
        props: { multiple: true },
        validation: { required: false },
        options: [
          { label: 'Man', value: 'man' },
          { label: 'Woman', value: 'woman' },
          { label: 'Cisgender', value: 'cisgender' },
          { label: 'Transgender', value: 'transgender' },
          { label: 'Demigender', value: 'demigender' },
          { label: 'Agender', value: 'agender' },
          { label: 'Two-spirit /Culturally specific third gender', value: 'thirdgender' },
          { label: 'Non-binary', value: 'non-binary' },
          { label: 'Genderqueer', value: 'genderqueer' },
          { label: 'I use a different term (specify)', value: 'other' },
        ],
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'gender.otherGender',
        dynamicLogic: inList('gender.gender', 'other'),
        copy: 'Specify',
      },
    ] as Field[],
  },
  [CommonQuestionKeys.MAILING_ADDRESS]: {
    key: CommonQuestionKeys.MAILING_ADDRESS,
    copy: { title: 'What is your mailing address?' },
    fields: [
      {
        type: FieldType.Address,
        key: CommonQuestionKeys.MAILING_ADDRESS,
      },
    ],
  },
  [CommonQuestionKeys.ADDRESS]: {
    key: CommonQuestionKeys.ADDRESS,
    copy: { title: 'What is your home address?' },
    fields: [
      {
        type: FieldType.Address,
        key: CommonQuestionKeys.ADDRESS,
      },
    ],
  },
  [CommonQuestionKeys.RACE]: {
    key: CommonQuestionKeys.RACE,
    copy: {
      title: 'What is your race and/or ethnicity?',
      intro: 'Please select as many options as apply to you.',
    },
    skippable: true,
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Race',
        key: 'race.race',
        props: { multiple: true, childMode: 'AlwaysAvailable' },
        options: RACES,
        validation: { required: false },
      },
      {
        type: FieldType.Typography,
        key: 'race.other.label',
        copy: 'Not shown in the list (specify):',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: or(RACES.map(({ value }) => inList('race.race', `${value}:other`))),
      },
      // TODO: We should think about ways to standardize this type of input if possible,
      // this is going to be a pain to express in our JSON language
      ...RACES.map(({ label, value }) => ({
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: `race.otherRace.${value}`,
        dynamicLogic: inList('race.race', `${value}:other`),
        copy: label,
        props: { helperText: OTHER_HELPER_TEXT[value] },
      })),
    ] as Field[],
  },
  [CommonQuestionKeys.HOUSEHOLD_MEMBERS]: {
    key: CommonQuestionKeys.HOUSEHOLD_MEMBERS,
    copy: {
      title: 'Please list the members of your household',
      intro: `For your application to be considered, you **MUST** list all household members 18 years of age and older.
Each household member is required to provide documentation for income and identity verification.`,
    },
    fields: [
      {
        type: FieldType.Complex,
        key: CommonQuestionKeys.HOUSEHOLD_MEMBERS,
        copy: 'Household Member',
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            key: 'firstName',
            copy: 'First Name',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            key: 'lastName',
            copy: 'Last Name',
          },
          {
            type: FieldType.Date,
            key: CommonQuestionKeys.DATE_OF_BIRTH,
            copy: 'Date of Birth',
          },
          {
            type: FieldType.Dropdown,
            key: 'relationship',
            copy: 'Your relationship with this person',
            options: [
              { label: 'Self', value: 'self' },
              { label: 'Partner', value: 'partner' },
              { label: 'Married to', value: 'married' },
              { label: 'Parent/Step parent of', value: 'parent' },
              { label: 'Child/Step child of', value: 'child' },
              { label: 'Related to in another way', value: 'related' },
              { label: 'Not related', value: 'not-related' },
            ],
          },
          {
            type: FieldType.Dropdown,
            key: 'education',
            copy: 'Highest level of education completed',
            options: [
              {
                label: 'Less than high school (0-8 years)',
                value: 'less-than-high-school-0-8-years',
              },
              {
                label: 'Some high school (9-12 years but did not graduate)',
                value: 'some-high-school-9-12-years-but-did-not-graduate',
              },
              { label: 'High School/GED', value: 'high-SchoolGED' },
              {
                label: 'Vocational or trade school',
                value: 'vocational-or-trade-school',
              },
              { label: 'Some college', value: 'some-college' },
              { label: '2-year college degree', value: '2-year-college-degree' },
              { label: '4-year college degree', value: '4-year-college-degree' },
              { label: 'Graduate degree', value: 'graduate-degree' },
              { label: 'Prefer not to answer', value: 'prefer-not-to-answer' },
            ],
          },
          {
            type: FieldType.Dropdown,
            key: 'workSituation',
            copy: 'Work situation within the last 3 months',
            props: { multiple: true },
            options: [
              { label: 'Employed full-time', value: 'employed-full-time' },
              { label: 'Employed part time', value: 'employed-part-time' },
              { label: 'Self-employed, full-time', value: 'self-employed-full-time' },
              { label: 'Self-employed, part time', value: 'self-employed-part-time' },
              { label: 'Student', value: 'student' },
              { label: 'Retired', value: 'retired' },
              { label: 'Not Applicable', value: 'not-applicable' },
            ],
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Currency,
            key: 'monthlyIncome',
            copy: 'Monthly income',
          },
          {
            type: FieldType.Document,
            key: 'incomeDocumentation',
          },
        ],
      },
      {
        type: FieldType.Calculated,
        key: 'householdIncome.annualized',
        display: {
          copy: 'Annual Household Income',
          inputType: TextInputType.Currency,
        },
        formula: {
          type: ExpressionType.Math,
          op: MathOperator.Multiply,
          expressions: [sum('householdMembers', 'monthlyIncome'), literal(12)],
        },
      },
    ],
  },
  [CommonQuestionKeys.PREFERRED_PAYMENT_METHOD]: {
    key: CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
    copy: {
      title: 'How would you like to receive your monthly payments?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
        options: [
          {
            label: 'ACH Direct Deposit',
            description:
              'If you’re selected, you will have the opportunity to provide your bank account and routing information so that we can issue payments directly to your bank account.',
            value: PaymentMethod.DirectDeposit,
          },
          {
            label: 'Prepaid Debit Card (Physical)',
            description:
              'If you’re selected, we will mail you a physical debit card. There are no restrictions on how you can use your debit card.',
            value: PaymentMethod.PhysicalCard,
          },
          {
            label: 'Prepaid Debit Card (Virtual) ',
            description:
              'If you’re selected, you will be emailed a link to claim your virtual debit card online. There are no restriction on how you can use your virtual debit card; however, you will be unable to use an ATM.',
            value: PaymentMethod.VirtualCard,
          },
        ],
      },
    ],
  },
} as StandardComponents['definitions']['questions'];
export default QUESTIONS;
